import styles from './WordLetterTemplatePicker.module.scss';

import Typography from '@mui/material/Typography';
import { useObservableState } from 'observable-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { useDataProvider } from '@work4all/data';

import { LetterTemplateGroup } from '@work4all/models/lib/Classes/LetterTemplateGroup.entity';
import { WordLetterTemplate } from '@work4all/models/lib/Classes/WordLetterTemplate.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { Chip } from '../../../dataDisplay/chip/Chip';
import { ChipList } from '../../../dataDisplay/chip/ChipList';
import {
  SelectableTree,
  TreeNode,
} from '../../../dataDisplay/tree/SelectableTree';
import {
  EntityPickerTab,
  EntityPickerTabBar,
  FilterTextInput,
} from '../components';
import { IPickerProps } from '../types';

export type IWordLetterTemplatePickerProps<TMultiple extends boolean> =
  IPickerProps<WordLetterTemplate, TMultiple> & {
    onChange: (value: WordLetterTemplate | WordLetterTemplate[]) => void;
  };

type WordLetterTemplateGroupUnion = Pick<
  LetterTemplateGroup & WordLetterTemplate,
  'id' | 'name' | 'templates' | 'body'
>;

const expandedNodesLocalStorageyKey =
  'w4aWordLetterTemplatePickerExpandedNodes';
const selectedTabLocalStorageyKey = 'w4aWordLetterTemplatePickerSelectedTab';

const saveExpandedNodes = (expandedNodes: string[]) => {
  localStorage.setItem(
    expandedNodesLocalStorageyKey,
    JSON.stringify(expandedNodes)
  );
};

const loadExpandedNodes = (): string[] => {
  const loadedNodes = localStorage.getItem(expandedNodesLocalStorageyKey);
  return loadedNodes ? JSON.parse(loadedNodes) : [];
};

const saveSelectedTab = (selectedTab: string) => {
  localStorage.setItem(selectedTabLocalStorageyKey, selectedTab);
};

const loadSelectedTab = (): 'letters' | 'serialLetters' => {
  const selectedTab = localStorage.getItem(selectedTabLocalStorageyKey);
  return selectedTab ? (selectedTab as 'letters' | 'serialLetters') : 'letters';
};

const cleanList = (
  serialLetters: boolean,
  groups: LetterTemplateGroup[]
): LetterTemplateGroup[] => {
  let result: LetterTemplateGroup[] = [];
  for (const group of groups) {
    const cleanedTemplates = group.templates
      .filter((x) => x.serialLetter === serialLetters)
      .sort((a, b) => {
        return a.body > b.body ? 1 : b.body > a.body ? -1 : 0;
      });

    if (cleanedTemplates.length) {
      result.push({ ...group, templates: cleanedTemplates });
    }
  }
  result = result.sort((a, b) => {
    return a.name > b.name ? 1 : b.name > a.name ? -1 : 0;
  });
  return result;
};

export function WordLetterTemplatePicker<TMultiple extends boolean>(
  props: IWordLetterTemplatePickerProps<TMultiple>
) {
  const { data, ...rest } = props;

  const { t } = useTranslation();

  const allFields = useMemo(() => {
    return {
      ...LETTER_TEMPLATE_GROUP_FIELDS,
      ...{ templates: [{ ...LETTER_TEMPLATES_FIELDS, ...data }] },
    };
  }, [data]);

  const requestData: DataRequest = useMemo(() => {
    return {
      entity: Entities.letterTemplateGroup,
      data: allFields,
      filter: props.prefilter,
      completeDataResponse: true,
    };
  }, [allFields, props.prefilter]);

  const responseData = useDataProvider<LetterTemplateGroup>(requestData);

  const [searchString, setSearchString] = useObservableState(
    (input$) => input$.pipe(distinctUntilChanged(), debounceTime(200)),
    ''
  );

  const [selectedIds, setSelectedIds] = useState([]);

  const nodeMap = useMemo(() => {
    const map = new Map<
      string,
      WordLetterTemplateGroupUnion & { parent: WordLetterTemplateGroupUnion }
    >();
    const arrayToMap = (
      elements: WordLetterTemplateGroupUnion[],
      map: Map<
        string,
        WordLetterTemplateGroupUnion & { parent: WordLetterTemplateGroupUnion }
      >,
      parent?: WordLetterTemplateGroupUnion
    ) => {
      elements?.forEach((el) => {
        map.set(el.id.toString(), { ...el, parent });
        if (el.templates?.length > 0) {
          arrayToMap(el.templates, map, el);
        }
      });
    };

    arrayToMap(responseData.data, map);
    return map;
  }, [responseData]);

  const [tab, setTab] = useState<'letters' | 'serialLetters'>('letters');

  const treeData = useMemo<TreeNode[]>(() => {
    const cleanedList = cleanList(tab === 'serialLetters', responseData.data);

    const toTree = (data: WordLetterTemplateGroupUnion[]) => {
      return data.map((el) => {
        const result: TreeNode = {
          id: el.id.toString(),
          label: el.body || el.name,
          children: el.templates ? toTree(el.templates) : undefined,
        };

        return result;
      }, []);
    };
    //return tree
    if (searchString.trim() === '') return toTree(cleanedList || []);

    //return flat list
    const searchResults = (cleanedList || [])
      .map((el) => ({
        parent: el,
        children: el.templates?.filter((el) =>
          el.body.toLowerCase().includes(searchString.toLowerCase())
        ),
      }))
      .flatMap(({ parent, children }) => {
        return children.map((el) => ({ el, parent, children }));
      })
      .sort((a, b) => {
        return a.el.body.toLocaleLowerCase() > b.el.body.toLocaleLowerCase()
          ? 1
          : a.el.body.toLocaleLowerCase() < b.el.body.toLocaleLowerCase()
          ? -1
          : 0;
      })
      .map((x) => {
        return {
          id: x.el.id.toString(),
          label: (
            <Typography component="div">
              <span>{x.el.body}</span>
              <span
                className={styles.secondaryItemLabel}
              >{` | ${x.parent.name}`}</span>
            </Typography>
          ),
        };
      });

    return searchResults;
  }, [responseData.data, searchString, tab]);

  const getWordLetterTemplateById = useCallback(
    (id: string) => {
      const templates = [];
      responseData.data.forEach((x) => templates.push(...x.templates));
      return templates.find((template) => template.id === parseInt(id));
    },
    [responseData.data]
  );

  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  useEffect(() => {
    setExpandedNodes(loadExpandedNodes());
  }, []);

  useEffect(() => {
    setTab(loadSelectedTab());
  }, []);

  return (
    <div>
      {props.multiple && selectedIds?.length > 0 && (
        <div className={styles.wrapper}>
          <ChipList>
            {selectedIds?.map((id) => {
              const data = nodeMap.get(id.toString());
              return <Chip key={id} maxWidth={18} label={data.name} />;
            })}
          </ChipList>
        </div>
      )}

      <div className={styles.wrapper}>
        <FilterTextInput
          placeholder="Suchen"
          onChange={(val) => {
            setSearchString(val);
          }}
        />
      </div>
      <EntityPickerTabBar
        value={tab}
        onChange={(val) => {
          setTab(val);
          saveSelectedTab(val);
        }}
      >
        <EntityPickerTab value="letters">{t('COMMON.LETTERS')}</EntityPickerTab>
        <EntityPickerTab value="serialLetters" disabled={true}>
          {t('COMMON.SERIALLETTERS')}
        </EntityPickerTab>
      </EntityPickerTabBar>

      <div className={styles.treeWrapper}>
        <SelectableTree
          onNodeToggle={(e, nodeIds) => {
            setExpandedNodes(nodeIds);
            saveExpandedNodes(nodeIds);
          }}
          expanded={expandedNodes}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          multiple={props.multiple as any}
          selectable="leaf"
          data={treeData}
          selected={selectedIds}
          onChange={(ids: string | string[]) => {
            setSelectedIds(Array.isArray(ids) ? ids : [ids]);

            if (Array.isArray(ids)) {
              props.onChange(ids.map((id) => getWordLetterTemplateById(id)));
            } else {
              props.onChange(getWordLetterTemplateById(ids));
            }
          }}
        />
      </div>
    </div>
  );
}

export const LETTER_TEMPLATES_FIELDS: WordLetterTemplate = {
  id: null,
  body: null,
  serialLetter: null,
};
const LETTER_TEMPLATE_GROUP_FIELDS: LetterTemplateGroup = {
  id: null,
  name: null,
  templates: [LETTER_TEMPLATES_FIELDS],
};
